import styles from './OrderEconomicsPanel.module.css';
import OrderFirstOrderValueSection from './OrderFirstOrderValueSection';
import {
  ECONOMICS_LINES,
  EconomicsRow,
  deriveProfitTone,
  formatDollars,
  formatLineSource,
  formatMargin,
  isAbsent
} from './OrderEconomicsRow';

/**
 * Stored per-order economics beside the preserved original — card 2257.
 *
 * 🔑 ONE COMPONENT, TWO MOUNTS. `OrderDetailPage` and `OrderInlineDetail` both render this
 * exact component from the SAME `/admin/orders/:id/detail` payload, so the list view and the
 * detail view cannot disagree. Two copies of this markup could drift; one cannot.
 *
 * 🔴 PURELY PRESENTATIONAL — IT DERIVES NO MONEY. Every figure here is read straight from a
 * stored `order_economics` / `order_cost_snapshots` column. Card 2260 has to reconcile these
 * numbers to the cent across five surfaces, which is impossible if a render layer quietly
 * recomputes a missing line. Absence is passed through as absence.
 *
 * 🪤 UNITS. `order_economics` and `order_cost_snapshots` money columns are `numeric(12,2)`
 * **DOLLARS**. `public.orders` stores **integer CENTS**. `OrderDetailPage` used to guess the unit
 * from the magnitude (`n > 999 ? n / 100 : n`); card 28334 replaced that with `formatCentsUsd`
 * in `web/pages/admin/orderCostMath.js`, which reads CENTS unconditionally. Either way the rule
 * is WRONG for this DOLLAR data — cents-formatting a stored $1,325.36 renders $13.25 — so
 * this file still formats dollars explicitly and never imports it.
 *
 * 🪤 `lineSources.<line>.value` is in integer CENTS even though the column beside it is in
 * dollars (the recorder builds a `*_cents` payload and the RPC converts). It is used here ONLY
 * for provenance text, never as a displayed amount.
 *
 * 🔑 THREE COLUMNS, THREE QUANTITIES (card 2459). The metric block published beside the other
 * two lives in `OrderFirstOrderValueSection`, and the row markup all three share lives in
 * `OrderEconomicsRow`. Splitting them keeps every file inside the 300-line standard and, more
 * usefully, makes it impossible for the three sections to render a row differently.
 */

/** `total_cost` and `profit` are NULL BY DESIGN on a snapshot — no such column exists on
 *  `public.orders`, so a number there would fabricate an original that never existed. */
const ORIGINAL_LINES = Object.freeze([
  { key: 'medicationCost', label: 'Medication' },
  { key: 'consultationCost', label: 'Consultation' },
  { key: 'shippingAndHandlingCost', label: 'Shipping & handling' },
  { key: 'miscCost', label: 'Misc' },
  { key: 'totalCost', label: 'Total cost' },
  { key: 'profit', label: 'Profit' }
]);

const formatTimestamp = (value) => {
  if (isAbsent(value)) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? String(value)
    : parsed.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
    });
};

/**
 * @param {object} props.view  the `economics` block from `/admin/orders/:id/detail`:
 *   `{ isRecalculated, economics, original, firstOrderValue }`. An order that has never been
 *   recalculated arrives as `economics: null` and MUST render as an explicit state, never as
 *   zeros; the same rule governs `firstOrderValue.value`.
 */
const OrderEconomicsPanel = ({ view }) => {
  const economics = view?.economics ?? null;
  const original = view?.original ?? null;
  const isRecalculated = Boolean(view?.isRecalculated && economics);
  const lineSources = economics?.lineSources ?? {};

  const calculatedAt = formatTimestamp(economics?.calculatedAt);
  const capturedAt = formatTimestamp(original?.capturedAt);
  const profitDollars = economics?.profit;

  return (
    <div className={styles.panel} data-testid="order-economics-panel">
      <h3 className={styles.title}>Order Economics</h3>
      <p className={styles.meta}>
        {isRecalculated
          ? `Stored engine output · economics_version ${economics.economicsVersion} · calculated_at ${calculatedAt}`
          : 'Canonical stored figures for this order, shown beside the original costs as charged.'}
      </p>

      <div className={styles.columns}>
        <div className={styles.column}>
          <p className={styles.sectionLabel} data-testid="stored-economics-label">
            Stored economics {isRecalculated ? `· v${economics.economicsVersion}` : ''}
          </p>

          {isRecalculated ? (
            <div data-testid="stored-economics-lines">
              {ECONOMICS_LINES.map(({ key, sourceKey, label, emphasis }) => {
                const line = lineSources?.[sourceKey] ?? null;
                return (
                  <EconomicsRow
                    key={key}
                    testId={sourceKey}
                    label={label}
                    amount={formatDollars(economics[key])}
                    quality={line?.quality}
                    source={formatLineSource(line)}
                    emphasis={emphasis}
                    tone={key === 'profit' ? deriveProfitTone(economics[key]) : null}
                  />
                );
              })}
              <EconomicsRow
                testId="margin"
                label="Margin"
                amount={formatMargin(economics.margin)}
                quality={economics.dataQuality}
                source={isAbsent(economics.margin)
                  ? 'undefined when net revenue is 0'
                  : 'derived · profit / net revenue'}
                tone={deriveProfitTone(profitDollars)}
              />
            </div>
          ) : (
            <div className={styles.notice} data-testid="economics-not-recalculated">
              Not yet recalculated
              <span className={styles.noticeHint}>
                This order has no stored <code>order_economics</code> row, so there are no
                canonical figures to show. This is an absence, not a zero.
              </span>
            </div>
          )}
        </div>

        <div className={styles.column}>
          <p className={styles.sectionLabel} data-testid="original-costs-label">
            Original (as charged)
          </p>

          {original ? (
            <div data-testid="original-costs-lines">
              {ORIGINAL_LINES.map(({ key, label }) => (
                <EconomicsRow
                  key={key}
                  testId={`original-${key}`}
                  label={label}
                  amount={formatDollars(original[key])}
                  source={null}
                />
              ))}
              <p className={styles.readOnlyNote}>
                Captured {capturedAt} from {original.sourceUnits === 'integer_cents' ? 'integer cents' : original.sourceUnits}.
                Read-only: this snapshot is insert-only and is never edited from this page.
                Total cost and profit are absent by design — neither exists as an original upstream value.
              </p>
            </div>
          ) : (
            <div className={styles.notice} data-testid="original-costs-missing">
              No original snapshot captured
              <span className={styles.noticeHint}>
                No <code>order_cost_snapshots</code> row exists for this order.
              </span>
            </div>
          )}
        </div>

        <OrderFirstOrderValueSection block={view?.firstOrderValue} />
      </div>
    </div>
  );
};

export default OrderEconomicsPanel;
